import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Fetches Belomotors public brand config (Twilio BDC phone + brand colors).
 *
 * On first mount, GET /api/public/config populates the shared cache. Every
 * consumer after that reuses the cache instantly. Falls back to the hard
 * defaults (used for the very first paint before the fetch resolves) so
 * headers/footers never render a blank phone number.
 */
const FALLBACK = {
  bdc_phone_display: "(978) 221-1451",
  bdc_phone_href: "tel:+19782211451",
  bdc_phone_e164: null,
  brand_color: "#1B3A6B",
  gold_color: "#D4A537",
};

let cached = null;
let inflight = null;
const subscribers = new Set();

const notify = () => {
  subscribers.forEach((fn) => fn(cached));
};

export const loadBrandConfig = async () => {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = api
    .get("/public/config")
    .then((r) => {
      cached = { ...FALLBACK, ...(r.data || {}) };
      notify();
      return cached;
    })
    .catch(() => {
      cached = { ...FALLBACK };
      notify();
      return cached;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const useBrandConfig = () => {
  const [config, setConfig] = useState(cached || FALLBACK);
  useEffect(() => {
    const listener = (v) => setConfig(v || FALLBACK);
    subscribers.add(listener);
    if (!cached) loadBrandConfig();
    return () => {
      subscribers.delete(listener);
    };
  }, []);
  return config;
};

export default useBrandConfig;
